/**
 * PATH: packages/email-hub/src/feature/components/rich-text/kanbanCardNode.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-CARDNODE-001
 * PURPOSE: A TipTap block node that embeds a Kanban card "chip" inside the
 *          email body. Round-trips as <div data-kanban-card …> so drafts
 *          persist; the send-time serialiser bakes it to a static anchor.
 * WHY:     Operator: "Kanban Cards should also be attachable to Email
 *          Bodies." The chip carries data-card-id so the send path can
 *          create the email<->card link (Allocation + related_emails).
 */

import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import type { NodeViewProps } from '@tiptap/react'
import { ClipboardList, X } from 'lucide-react'

export interface KanbanCardAttrs {
  cardId: string
  title: string
  subtitle: string
  status: string
  url: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    kanbanCard: {
      insertKanbanCard: (attrs: KanbanCardAttrs) => ReturnType
    }
  }
}

function CardChipView({ node, deleteNode, editor }: NodeViewProps) {
  const { title, subtitle, status } = node.attrs as KanbanCardAttrs
  return (
    <NodeViewWrapper
      data-kanban-card-view=""
      className="my-1.5 inline-flex max-w-full items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 align-top dark:border-indigo-500/40 dark:bg-indigo-500/10"
    >
      <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-300" aria-hidden />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-indigo-900 dark:text-indigo-100">{title}</div>
        {(status || subtitle) && (
          <div className="truncate text-[11px] text-indigo-600/90 dark:text-indigo-300/80">
            {[status, subtitle].filter(Boolean).join(' \u00B7 ')}
          </div>
        )}
      </div>
      {editor?.isEditable && (
        <button
          type="button"
          onClick={() => deleteNode()}
          className="ml-1 shrink-0 rounded p-0.5 text-indigo-400 hover:bg-indigo-100 hover:text-indigo-700 dark:hover:bg-indigo-500/20"
          aria-label="Remove card"
          title="Remove card"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </NodeViewWrapper>
  )
}

export const KanbanCardNode = Node.create({
  name: 'kanbanCard',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      cardId: { default: '', parseHTML: (el) => el.getAttribute('data-card-id') || '', renderHTML: (a) => ({ 'data-card-id': a.cardId }) },
      title: { default: '', parseHTML: (el) => el.getAttribute('data-card-title') || '', renderHTML: (a) => ({ 'data-card-title': a.title }) },
      subtitle: { default: '', parseHTML: (el) => el.getAttribute('data-card-subtitle') || '', renderHTML: (a) => ({ 'data-card-subtitle': a.subtitle }) },
      status: { default: '', parseHTML: (el) => el.getAttribute('data-card-status') || '', renderHTML: (a) => ({ 'data-card-status': a.status }) },
      url: { default: '', parseHTML: (el) => el.getAttribute('data-card-url') || '', renderHTML: (a) => ({ 'data-card-url': a.url }) },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-kanban-card]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-kanban-card': '' })]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CardChipView)
  },

  addCommands() {
    return {
      insertKanbanCard:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    }
  },
})
